import { useAuthStore } from '@/state/authStore';
import { useGameStore } from '@/state/gameStore';
import type { Mission } from '@/state/types';

/**
 * Phase 16 Hot-Fix — Tester-Tracking via Heartbeat-Posts.
 *
 * - `initHeartbeat()` startet den 30 s-Periodischen + Visibility-Listener.
 *   Sendet POST /api/heartbeat solange ein Auth-Token vorhanden ist.
 * - `attachGameEventSubscriptions()` hört auf gameStore-Änderungen und
 *   triggert Mission-Complete- und Slot-Created-Events.
 * - `reportMissionComplete(code, success)` + `reportSlotCreated()` als
 *   imperative Hooks, falls direkter Zugriff sinnvoller ist als die
 *   Subscription-Variante.
 */

const HEARTBEAT_INTERVAL_MS = 30_000;

let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
let visibilityListener: (() => void) | null = null;
let visibilityStartTime = Date.now();
let isCurrentlyVisible =
  typeof document !== 'undefined' ? document.visibilityState === 'visible' : false;

export interface HeartbeatOptions {
  visibleSinceSec: number;
  event: 'periodic' | 'visibility' | 'mission-complete' | 'mission-fail' | 'slot-created';
  missionData?: { missionType: string; success: boolean };
}

export function initHeartbeat(): void {
  if (typeof document === 'undefined') return;
  if (heartbeatInterval) return; // schon initialisiert

  visibilityStartTime = Date.now();
  isCurrentlyVisible = document.visibilityState === 'visible';

  visibilityListener = () => {
    const now = Date.now();
    const wasVisible = isCurrentlyVisible;
    const becameVisible = document.visibilityState === 'visible';
    if (wasVisible && !becameVisible) {
      const visibleSec = Math.floor((now - visibilityStartTime) / 1000);
      void sendHeartbeat({ visibleSinceSec: visibleSec, event: 'visibility' });
      isCurrentlyVisible = false;
    } else if (!wasVisible && becameVisible) {
      visibilityStartTime = now;
      isCurrentlyVisible = true;
      void sendHeartbeat({ visibleSinceSec: 0, event: 'visibility' });
    }
  };
  document.addEventListener('visibilitychange', visibilityListener);

  heartbeatInterval = setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    const visibleSec = Math.floor((now - visibilityStartTime) / 1000);
    visibilityStartTime = now; // Reset für nächstes Intervall
    void sendHeartbeat({ visibleSinceSec: visibleSec, event: 'periodic' });
  }, HEARTBEAT_INTERVAL_MS);

  // Initial visibility-Heartbeat (markiert eine neue Session).
  void sendHeartbeat({ visibleSinceSec: 0, event: 'visibility' });
}

export function shutdownHeartbeat(): void {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
  if (visibilityListener && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', visibilityListener);
    visibilityListener = null;
  }
}

async function sendHeartbeat(opts: HeartbeatOptions): Promise<void> {
  const auth = useAuthStore.getState();
  if (!auth.isAuthenticated || !auth.token) return;
  try {
    await fetch('/api/heartbeat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token}`,
      },
      body: JSON.stringify(opts),
    });
  } catch {
    // Fail silent — Heartbeats dürfen kein User-facing Issue verursachen.
  }
}

/* ─── Imperative Game-Events (für direkte Aufrufe aus Actions) ─── */

export function reportMissionComplete(missionCode: string, success: boolean): void {
  void sendHeartbeat({
    visibleSinceSec: 0,
    event: success ? 'mission-complete' : 'mission-fail',
    missionData: { missionType: missionCode, success },
  });
}

export function reportSlotCreated(): void {
  void sendHeartbeat({
    visibleSinceSec: 0,
    event: 'slot-created',
  });
}

/* ─── State-Subscription (entkoppelt von gameStore-Actions) ─── */

let unsubGame: (() => void) | null = null;

/**
 * Hängt eine zustand-Subscription an den gameStore, die:
 *  - Mission-Status-Übergänge (active → closed/failed) erkennt und
 *    `reportMissionComplete` triggert,
 *  - Slot-Initialisierung (homeCity geht von leer → gesetzt) erkennt und
 *    `reportSlotCreated` triggert.
 */
export function attachGameEventSubscriptions(): void {
  if (unsubGame) return;
  let prev = useGameStore.getState();
  unsubGame = useGameStore.subscribe((state) => {
    // Mission-Übergänge
    const prevById = new Map<string, Mission>(prev.missions.map((m) => [m.id, m]));
    for (const m of state.missions) {
      const before = prevById.get(m.id);
      if (!before) continue;
      const wasActive =
        before.status === 'open' ||
        before.status === 'dispatched' ||
        before.status === 'onscene';
      if (wasActive && (m.status === 'closed' || m.status === 'failed')) {
        reportMissionComplete(m.code, m.status === 'closed');
      }
    }
    // Slot-Init: homeCity transition
    if (!prev.homeCity && state.homeCity) {
      reportSlotCreated();
    }
    prev = state;
  });
}

export function detachGameEventSubscriptions(): void {
  if (unsubGame) {
    unsubGame();
    unsubGame = null;
  }
}

/* ─── UI-Helper ─── */

/** Sekunden → "12s" / "5m" / "1h 23m" / "2h". */
export function formatPlayTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0s';
  const sec = Math.floor(seconds);
  if (sec < 60) return `${sec}s`;
  const minutes = Math.floor(sec / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMin = minutes % 60;
  return remainingMin > 0 ? `${hours}h ${remainingMin}m` : `${hours}h`;
}
